import {
  Avatar,
  Badge,
  Button,
  Card,
  Chip,
  Drawer,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconSearch, IconUserPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { canEditUser, canGrantOperator } from '../access.ts';
import { api, unwrap } from '../api.ts';
import {
  type ListedUser,
  USER_FILTERS,
  type UserFilter,
  filterUsers,
  initials,
  relativeTime,
  roleLabel,
  userFilterLabel,
} from '../format.ts';
import { Empty, Failure, Loading, Page, useMe, useSave } from '../ui.tsx';

type User = ListedUser & { organizationId: string; lastSeenAt: string | null };

const STATUS_COLOR = { active: 'green', invited: 'blue', deactivated: 'gray' } as const;

function InviteForm(props: { canGrantOperator: boolean; onDone: () => void }) {
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    role: 'member' as 'admin' | 'member',
    isOperator: false,
  });
  const invite = useSave({
    run: () =>
      unwrap(
        api().POST('/v1/users', {
          body: {
            email: form.email,
            role: form.role,
            ...(form.displayName ? { displayName: form.displayName } : {}),
            ...(form.isOperator ? { isOperator: true } : {}),
          },
        }),
      ),
    invalidates: [['users']],
    success: (result) =>
      result.created
        ? `Invited ${result.user.email}`
        : `${result.user.email} already has access; nothing changed`,
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    invite.mutate(undefined);
  };
  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Email"
          type="email"
          placeholder="name@company.com"
          required
          data-autofocus
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.currentTarget.value })}
        />
        <TextInput
          label="Name"
          description="Optional; their identity provider fills it in at first sign-in"
          value={form.displayName}
          onChange={(event) => setForm({ ...form, displayName: event.currentTarget.value })}
        />
        <div>
          <Text size="sm" fw={500} mb={4}>
            Role
          </Text>
          <SegmentedControl
            fullWidth
            value={form.role}
            onChange={(role) => setForm({ ...form, role: role as 'admin' | 'member' })}
            data={[
              { value: 'member', label: 'User' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
        </div>
        {props.canGrantOperator ? (
          <Switch
            label="Global admin (Budovan staff)"
            description="Sees and manages every organization"
            checked={form.isOperator}
            onChange={(event) => setForm({ ...form, isOperator: event.currentTarget.checked })}
          />
        ) : null}
        <Text size="xs" c="dimmed">
          They get the role when they first sign in with this email. Nothing is sent from here yet; share the
          sign-in link yourself.
        </Text>
        <Failure error={invite.error} />
        <Button type="submit" loading={invite.isPending} fullWidth>
          Invite
        </Button>
      </Stack>
    </form>
  );
}

function confirmDanger(title: string, body: string, label: string, onConfirm: () => void) {
  modals.openConfirmModal({
    title,
    children: <Text size="sm">{body}</Text>,
    labels: { confirm: label, cancel: 'Cancel' },
    confirmProps: { color: 'red' },
    onConfirm,
  });
}

function UserEditor(props: { user: User; canGrantOperator: boolean; onDone: () => void }) {
  const { user } = props;
  const update = useSave({
    run: (body: { role?: 'admin' | 'member'; isOperator?: boolean; status?: 'active' | 'deactivated' }) =>
      unwrap(api().PATCH('/v1/users/{userId}', { params: { path: { userId: user.id } }, body })),
    invalidates: [['users'], ['audit']],
    success: 'Access updated',
  });
  const revoke = useSave({
    run: () =>
      unwrap(api().DELETE('/v1/invitations/{invitationId}', { params: { path: { invitationId: user.id } } })),
    invalidates: [['users'], ['audit']],
    success: 'Invitation revoked',
    onDone: props.onDone,
  });
  const name = user.displayName ?? user.email ?? 'this user';
  return (
    <Stack gap="lg">
      <Group>
        <Avatar size="lg" radius="xl" color="ink">
          {initials(user.displayName, user.email)}
        </Avatar>
        <div>
          <Text fw={700}>{user.displayName ?? user.email}</Text>
          <Text size="sm" c="dimmed">
            {user.email}
          </Text>
          <Badge mt={4} variant="light" color={STATUS_COLOR[user.status]}>
            {user.status}
          </Badge>
        </div>
      </Group>
      <div>
        <Text size="sm" fw={500} mb={4}>
          Role
        </Text>
        <SegmentedControl
          fullWidth
          value={user.role}
          disabled={update.isPending}
          onChange={(role) => update.mutate({ role: role as 'admin' | 'member' })}
          data={[
            { value: 'member', label: 'User' },
            { value: 'admin', label: 'Admin' },
          ]}
        />
      </div>
      {props.canGrantOperator ? (
        <Switch
          label="Global admin (Budovan staff)"
          description="Sees and manages every organization"
          checked={user.isOperator}
          disabled={update.isPending}
          onChange={(event) => update.mutate({ isOperator: event.currentTarget.checked })}
        />
      ) : null}
      <Failure error={update.error} />
      <Text size="sm" c="dimmed">
        Last sign-in: {relativeTime(user.lastSeenAt)}
      </Text>
      {user.status === 'invited' ? (
        <Button
          color="red"
          variant="light"
          loading={revoke.isPending}
          onClick={() =>
            confirmDanger(
              'Revoke invitation?',
              `${name} will not get access when they sign in.`,
              'Revoke',
              () => revoke.mutate(undefined),
            )
          }
        >
          Revoke invitation
        </Button>
      ) : null}
      {user.status === 'active' ? (
        <Button
          color="red"
          variant="light"
          onClick={() =>
            confirmDanger(
              'Turn off access?',
              `${name} is signed out at their next request and cannot sign in until you turn access back on. Their bookings stay.`,
              'Deactivate',
              () => update.mutate({ status: 'deactivated' }),
            )
          }
        >
          Deactivate
        </Button>
      ) : null}
      {user.status === 'deactivated' ? (
        <Button variant="default" onClick={() => update.mutate({ status: 'active' })}>
          Turn access back on
        </Button>
      ) : null}
    </Stack>
  );
}

export function UsersPage() {
  const me = useMe();
  const search = useSearch({ from: '/users' });
  const navigate = useNavigate({ from: '/users' });
  const filter: UserFilter = search.filter ?? 'all';
  const query = search.q ?? '';
  const users = useQuery({ queryKey: ['users'], queryFn: () => unwrap(api().GET('/v1/users')) });
  const [inviting, invite] = useDisclosure(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (!me.data) return <Loading />;
  const viewer = me.data;
  const editing = users.data?.find((user) => user.id === editingId) ?? null;
  const shown = filterUsers(users.data ?? [], filter, query);
  const setSearch = (next: { filter?: UserFilter; q?: string }) =>
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true });

  return (
    <Page
      title="Users"
      description="People in your organization and what they may do. Roles are granted here, not in the identity provider."
      actions={
        <Button leftSection={<IconUserPlus size={16} />} onClick={invite.open}>
          Add new user
        </Button>
      }
    >
      <Card withBorder padding="lg">
        <Stack>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder="Search"
            aria-label="Search users"
            value={query}
            onChange={(event) => setSearch({ q: event.currentTarget.value })}
          />
          <Chip.Group value={filter} onChange={(value) => setSearch({ filter: value as UserFilter })}>
            <Group gap="xs">
              {USER_FILTERS.map((option) => (
                <Chip key={option} value={option} radius="xl">
                  {userFilterLabel(option)}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          {users.isPending ? <Loading /> : null}
          <Failure error={users.error} />
          {users.data && shown.length === 0 ? <Empty>No users match.</Empty> : null}
          {shown.length > 0 ? (
            <Table.ScrollContainer minWidth={640}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Last sign-in</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((user) => (
                    <Table.Tr key={user.id}>
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar radius="xl" color="ink">
                            {initials(user.displayName, user.email)}
                          </Avatar>
                          <div>
                            <Text fw={600} size="sm">
                              {user.displayName ?? user.email}
                            </Text>
                            {user.displayName ? (
                              <Text size="xs" c="dimmed">
                                {user.email}
                              </Text>
                            ) : null}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant={user.isOperator ? 'filled' : 'light'} color="ink">
                          {roleLabel(user)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="dot" color={STATUS_COLOR[user.status]}>
                          {user.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {user.status === 'invited' ? '—' : relativeTime(user.lastSeenAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {canEditUser(viewer, user) ? (
                          <Button size="xs" onClick={() => setEditingId(user.id)}>
                            Edit
                          </Button>
                        ) : (
                          <Text size="xs" c="dimmed">
                            You
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : null}
        </Stack>
      </Card>
      <Modal opened={inviting} onClose={invite.close} title="Add new user" centered>
        <InviteForm canGrantOperator={canGrantOperator(viewer)} onDone={invite.close} />
      </Modal>
      <Drawer opened={editing !== null} onClose={() => setEditingId(null)} title="Edit user" position="right">
        {editing ? (
          <UserEditor
            user={editing}
            canGrantOperator={canGrantOperator(viewer)}
            onDone={() => setEditingId(null)}
          />
        ) : null}
      </Drawer>
    </Page>
  );
}
